import React, { useEffect, useState } from 'react';
import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import { ArrowLeft, AlertCircle, WandSparkles, Settings } from 'lucide-react';
import {
  StepAiAnalysis,
  StepActionButton,
  StepAlert,
  DiscreteActionsDropdown,
  StepSkipButton,
} from '../components/ApplicationStepComponents';
import { UnitedStatesState, policyStepConfigs, SessionType } from '@shared';
import { FundingPolicyType, PREFILL_COMPLETION_STATUS, PREFILL_FORM_TYPE } from '@shared';

// Consolidated policy-application components import
import {
  PolicyApplicationStepper,
  SubmitPolicyApplicationFormStep,
  SignApplicationFormStep,
  ApplicationCompleteStep,
  PolicyApplicationLoadingState,
  MissingParametersError,
  MissingStepsError,
  SubmissionMethod,
  ConsentFormStep,
  DownloadPolicyIllustrationApplicationStep,
} from '../components/policy-application';
import { getStepActionVerb } from '../components/policy-application/stepUtils';

// Custom hooks
import { useClientPolicyApplication } from '../hooks/useClientPolicyApplication';
import { usePolicyApplicationSteps } from '../hooks/usePolicyApplicationSteps';
import { usePolicyApplicationPrefillForm } from '@client/hooks/usePolicyApplicationPrefillForm';
import { usePolicyApplicationStepSelection } from '../hooks/usePolicyApplicationStepSelection';
import { useEnsureDownloadPolicyIllustrationStep } from '../hooks/useEnsureDownloadPolicyIllustrationStep';
import { usePolicyFormMetadata } from '@client/hooks/usePolicyFormMetadata';
import { useApplyStaticPrefill } from '@client/hooks/useApplyStaticPrefill';
import { DialogModal } from '@client/components';
import { useToast } from '@client/components/hooks/use-toast';
import { parseSourceQuote } from '@client/util/quoteUtils';
import { useSelector } from 'react-redux';
import {
  selectPolicyApplicationsStatus,
  selectMaybeClientPartner,
  selectSession,
} from '@client/model';

const PREFILL_LOADING_TEXT =
  "We're using your intake survey to fill in your application. It will take less than 2 minutes.";
const PARTNER_PREFILL_LOADING_TEXT = "Processing your partner's intake now — just a moment more.";

interface StepAction {
  type: 'primary' | 'secondary' | 'skip' | 'test';
  label: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  forClientSkip?: boolean;
}

interface StepContentWrapperProps {
  title: string;
  description: string;
  children: React.ReactNode;
  actions?: StepAction[];
  stepStatus?: string;
}

const StepContentWrapper: React.FC<StepContentWrapperProps> = ({
  title,
  description,
  children,
  actions,
  stepStatus,
}) => {
  const session = useSelector(selectSession);
  const isWizard = session.sessionType === SessionType.wizard;
  return (
    <div className="p-2">
      <div className="mb-4">
        <h3 className="mb-1 text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>

      <div className="mb-8">{children}</div>

      {actions && actions.length > 0 && (
        <div className="flex justify-end gap-4 pt-4">
          <div className="flex flex-wrap justify-end gap-4">
            {/* Render skip dropdown first (only for wizard users and if step is not already skipped) */}
            {isWizard &&
              stepStatus !== 'skipped' &&
              stepStatus !== 'completed' &&
              actions.some(action => action.type === 'skip' || action.type === 'test') && (
                <DiscreteActionsDropdown
                  actions={actions
                    .filter(action => action.type === 'skip' || action.type === 'test')
                    .map(action => ({
                      id: action.label.toLowerCase().replace(/\s+/g, '-'),
                      label: action.label,
                      icon:
                        action.type === 'skip' ? (
                          <WandSparkles className="h-4 w-4" />
                        ) : (
                          <Settings className="h-4 w-4" />
                        ),
                      onClick: action.onClick,
                      disabled: action.disabled,
                      isLoading: action.isLoading,
                      loadingText: action.type === 'skip' ? 'Processing...' : 'Running...',
                    }))}
                />
              )}

            {/* Then render primary/secondary buttons */}
            {actions
              .filter(action => action.type !== 'skip' && action.type != 'test')
              .map((action, index) => {
                if (!action.forClientSkip) {
                  return (
                    <StepActionButton
                      key={`action-${index}`}
                      onClick={action.onClick}
                      disabled={action.disabled}
                      variant={action.type === 'secondary' ? 'outline' : 'default'}
                    >
                      {action.label}
                    </StepActionButton>
                  );
                }

                if (
                  action.forClientSkip &&
                  stepStatus !== 'skipped' &&
                  stepStatus !== 'completed'
                ) {
                  return (
                    <StepSkipButton
                      key={`action-${index}`}
                      onClick={action.onClick}
                      isLoading={action.isLoading}
                    >
                      {action.label}
                    </StepSkipButton>
                  );
                }
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export function ClientPolicyApplication() {
  const [showAIConsentDialog, setShowAIConsentDialog] = useState(false);
  const [prefillProcessingNotification, setPrefillProcessingNotification] =
    useState<string>(PREFILL_LOADING_TEXT);
  const [selectedSubmissionMethod, setSelectedSubmissionMethod] = useState<SubmissionMethod>(null);
  const policyApplicationStatus = useSelector(selectPolicyApplicationsStatus);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const isSavingCompleted = policyApplicationStatus === 'savingStepStatusComplete';

  // Use custom hooks for data and handlers
  const {
    clientId,
    clientPolicyApplicationId,
    client,
    policyApplication,
    fundingSourceDef,
    steps,
    currentStep: latestStep,
    underwritingFormId,
    applicationFormId,
    isLoading,
    allStepsCompleted,
    policyFundingSource,
    policyType,
    needsIllustrationBeforeSigning,
  } = useClientPolicyApplication();

  // Fetch form metadata from API (or cache)
  const { data: underwritingFormMetadata } = usePolicyFormMetadata(underwritingFormId || '');
  const { data: applicationFormMetadata } = usePolicyFormMetadata(applicationFormId || '');

  const { selectedStepId, setSelectedStepId } = usePolicyApplicationStepSelection({
    latestStep,
    allStepsCompleted,
  });

  // Get carrier from funding source
  const carrier = fundingSourceDef?.carrier || '';

  // Initialize static prefill hook for underwriting form
  const { applyStaticPrefill: applyUnderwritingStaticPrefill } = useApplyStaticPrefill({
    carrier,
    clientInfo: client,
    policyFundingSource: policyFundingSource || null,
    clientPolicyApplicationId: clientPolicyApplicationId || '',
    formId: underwritingFormId || '',
    policyType,
  });

  // Initialize static prefill hook for application form
  const { applyStaticPrefill: applyApplicationStaticPrefill } = useApplyStaticPrefill({
    carrier,
    clientInfo: client,
    policyFundingSource: policyFundingSource || null,
    clientPolicyApplicationId: clientPolicyApplicationId || '',
    formId: applicationFormId || '',
    policyType,
  });

  // Ensure backward compatibility for existing applications
  useEnsureDownloadPolicyIllustrationStep({
    policyApplication,
    clientPolicyApplicationId,
    steps,
  });

  const {
    handleGoHome,
    handleStartUnderwritingForm,
    handleSkipUnderwritingForm,
    handleStartApplicationForm,
  } = usePolicyApplicationSteps({
    clientId,
    clientPolicyApplicationId,
    underwritingFormId,
    applicationFormId,
    client,
    policyApplication,
  });

  const selectedStep = selectedStepId
    ? steps?.find(step => step.stepType === selectedStepId)
    : latestStep;

  const [isTestDocusignDataModalOpen, setIsTestDocusignDataModalOpen] = useState(false);

  const [prefillParams, setPrefillParams] = useState<PrefillParams>({
    clientId: '',
    clientPolicyApplicationId: '',
    formId: '',
    policyFormType: 'underwriting',
    fundingPolicyType: undefined,
    policyFundingSource: undefined,
  });

  const {
    fetchPrefillData,
    isLoading: isPrefillLoading,
    error: prefillError,
    status: prefillStatus,
  } = usePolicyApplicationPrefillForm(prefillParams);
  const { toast, errorToast } = useToast();

  useEffect(() => {
    if (prefillStatus === PREFILL_COMPLETION_STATUS.COMPLETED) {
      toast({
        title: 'Prefill Completed',
        description: 'Your application has been prefilled with available data.',
      });
      setPrefillProcessingNotification(PREFILL_LOADING_TEXT);
      handleFormNavigation(prefillParams.clientId!);
    } else if (prefillStatus === PREFILL_COMPLETION_STATUS.PROCESSING_PARTNER)
      setPrefillProcessingNotification(PARTNER_PREFILL_LOADING_TEXT);
    else if (prefillStatus === PREFILL_COMPLETION_STATUS.ERROR && prefillError) {
      errorToast('Prefill Failed', prefillError + '. Please fill out the form manually.');
      handleFormNavigation(prefillParams.clientId!);
    }
  }, [prefillStatus, handleStartUnderwritingForm, handleStartApplicationForm, prefillError]);

  useEffect(() => {
    if (isSavingCompleted && selectedStep?.stepType === 'submit-pre-underwriting') {
      setSelectedStepId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSavingCompleted]);

  const handleStartForm = (
    applicantClientId: string,
    applicantFormId: string,
    formType: 'application' | 'underwriting',
  ) => {
    if (
      (selectedStep?.status === 'not_started' || selectedStep?.status === 'skipped') &&
      isPrefillEligibleForm()
    ) {
      const isApplicationForm = formType === 'application';
      setPrefillParams({
        clientId: applicantClientId!,
        clientPolicyApplicationId: clientPolicyApplicationId!,
        formId: applicantFormId!,
        policyFormType: isApplicationForm
          ? PREFILL_FORM_TYPE.APPLICATION
          : PREFILL_FORM_TYPE.UNDERWRITING,
        partnerClientId:
          isApplicationForm &&
          (policyType === FundingPolicyType.jointCareMatters ||
            policyType === FundingPolicyType.jointAssetCare) &&
          client.partnerClientId
            ? client.partnerClientId
            : undefined,
        fundingPolicyType: policyType,
        policyFundingSource: policyFundingSource,
      });
      setShowAIConsentDialog(true);
    } else {
      handleFormNavigation(applicantClientId!);
    }
  };

  const isPrefillEligibleForm = (): boolean => {
    if (
      policyApplication?.fundingPolicyType === FundingPolicyType.careMattersTwo ||
      policyApplication?.fundingPolicyType === FundingPolicyType.jointCareMatters ||
      policyApplication?.fundingPolicyType === FundingPolicyType.assetCare ||
      policyApplication?.fundingPolicyType === FundingPolicyType.jointAssetCare ||
      policyApplication?.fundingPolicyType === FundingPolicyType.careScoutCareAssurance ||
      policyApplication?.fundingPolicyType === FundingPolicyType.secureCareThree
    )
      return true;

    toast({
      title: 'AI Prefill coming soon!',
      description: `We're building support for AI prefill on this policy type. Hang tight!`,
    });
    return false;
  };

  const handleAcceptAIPrefill = () => {
    setShowAIConsentDialog(false);
    fetchPrefillData();
  };

  const handleDeclineAIPrefill = () => {
    setShowAIConsentDialog(false);

    // Apply static prefill when user declines AI
    if (selectedStep?.stepType === 'application-form') {
      applyApplicationStaticPrefill();
    } else {
      applyUnderwritingStaticPrefill();
    }

    // Navigate to the form
    handleFormNavigation(prefillParams.clientId!);
  };

  const handleFormNavigation = (clientId?: string) => {
    if (selectedStep?.stepType === 'application-form') {
      handleStartApplicationForm();
    } else {
      handleStartUnderwritingForm(clientId!);
    }
  };

  // Early returns for loading and error states
  if (!clientId || !clientPolicyApplicationId) {
    return <MissingParametersError onGoHome={handleGoHome} />;
  }

  if (isLoading) {
    return <PolicyApplicationLoadingState />;
  }

  if (isPrefillLoading) {
    return (
      <PolicyApplicationLoadingState
        title="Hang tight, we're prefilling your form..."
        message={prefillProcessingNotification}
      />
    );
  }

  if (!policyApplication || !fundingSourceDef) {
    return <MissingParametersError onGoHome={handleGoHome} />;
  }

  if (!steps) {
    return (
      <MissingStepsError
        policyType={fundingSourceDef.label}
        onGoHome={handleGoHome}
      />
    );
  }

  const requiresPreUnderwriting =
    (policyType && policyStepConfigs[policyType]?.includePreUnderwriting) || false;

  if (
    (requiresPreUnderwriting && !underwritingFormId) ||
    (policyApplication.stateOfResidenceAbbreviation && !applicationFormId)
  ) {
    return <MissingParametersError onGoHome={handleGoHome} />;
  }

  const renderStepContent = (step?: PolicyApplicationStep) => {
    const stepToRender = step || selectedStep;

    if (allStepsCompleted && !selectedStepId) {
      return <ApplicationCompleteStep onGoHome={handleGoHome} />;
    }

    if (!stepToRender) {
      return <ApplicationCompleteStep onGoHome={handleGoHome} />;
    }

    // Otherwise, render based on the determined step
    switch (stepToRender.stepType) {
      case 'download-policy-illustration': {
        if (!client || !policyApplication || !policyFundingSource) {
          return (
            <StepContentWrapper
              title="Download Policy Illustration"
              description="Loading policy information..."
              stepStatus={stepToRender.status}
            >
              <div className="text-gray-500">Loading...</div>
            </StepContentWrapper>
          );
        }

        const parsedSourceQuote = parseSourceQuote(policyFundingSource.sourceQuote);

        if (!parsedSourceQuote) {
          return (
            <StepContentWrapper
              title="Download Policy Illustration"
              description="Unable to load quote information."
              stepStatus={stepToRender.status}
            >
              <div className="text-red-600">Error: Quote data is not available.</div>
            </StepContentWrapper>
          );
        }

        return (
          <DownloadPolicyIllustrationApplicationStep
            clientPolicyApplicationId={clientPolicyApplicationId!}
            onContinue={() => setSelectedStepId(null)}
            sourceQuote={parsedSourceQuote}
            clientPartner={clientPartner}
            step={stepToRender}
            fundingSourceLabel={fundingSourceDef.label}
            onStepSelect={setSelectedStepId}
            client={client}
            policyApplication={policyApplication}
            needsIllustrationBeforeSigning={needsIllustrationBeforeSigning}
          />
        );
      }
      case 'pre-underwriting-form': {
        const isSkipping = policyApplicationStatus === 'savingStepStatus';
        const title = 'Pre-Underwriting Form';

        const actions: StepAction[] = [
          {
            type: 'secondary',
            label: 'Skip',
            onClick: () => handleSkipUnderwritingForm('pre-underwriting-form'),
            isLoading: isSkipping,
            forClientSkip: true,
          },
          {
            type: 'primary',
            label: title,
            onClick: () => handleStartForm(clientId!, underwritingFormId!, 'underwriting'),
            disabled: !underwritingFormId,
          },
          {
            type: 'skip',
            label: 'Skip',
            onClick: () => handleSkipUnderwritingForm('pre-underwriting-form'),
            isLoading: isSkipping,
          },
        ];

        return (
          <StepContentWrapper
            title="Pre-Underwriting Form"
            description={`Complete the pre-underwriting form to begin your ${fundingSourceDef.label} policy application process.`}
            actions={actions}
            stepStatus={stepToRender.status}
          >
            {!underwritingFormId && (
              <StepAlert
                title="Form Not Available"
                description={`The pre-underwriting form is not yet available for ${fundingSourceDef.label} policies. Please contact support for assistance.`}
                variant="error"
              />
            )}
          </StepContentWrapper>
        );
      }
      case 'partner-pre-underwriting-form': {
        const isSkipping = policyApplicationStatus === 'savingStepStatus';
        const title = 'Pre-Underwriting Form for Partner';

        const actions: StepAction[] = [
          {
            type: 'secondary',
            label: 'Skip',
            onClick: () => handleSkipUnderwritingForm('partner-pre-underwriting-form'),
            isLoading: isSkipping,
            forClientSkip: true,
          },
          {
            type: 'primary',
            label: title,
            onClick: () =>
              handleStartForm(client.partnerClientId ?? '', underwritingFormId!, 'underwriting'),
            disabled: !underwritingFormId,
          },
          {
            type: 'skip',
            label: `Skip`,
            onClick: () => handleSkipUnderwritingForm('partner-pre-underwriting-form'),
            isLoading: isSkipping,
          },
        ];

        return (
          <StepContentWrapper
            title="Pre-Underwriting Form for Partner"
            description={`Complete the pre-underwriting form to begin your ${fundingSourceDef.label} policy application process.`}
            actions={actions}
            stepStatus={stepToRender.status}
          >
            {!underwritingFormId && (
              <StepAlert
                title="Form Not Available"
                description={`The pre-underwriting form is not yet available for ${fundingSourceDef.label} policies. Please contact support for assistance.`}
                variant="error"
              />
            )}
          </StepContentWrapper>
        );
      }
      case 'submit-pre-underwriting': {
        return (
          <StepContentWrapper
            title="Submit Pre-Underwriting Form"
            description="Review and submit your pre-application information to proceed to the full application."
            stepStatus={stepToRender.status}
          >
            {underwritingFormId && underwritingFormMetadata ? (
              <SubmitPolicyApplicationFormStep
                clientPolicyApplicationId={clientPolicyApplicationId}
                policyFormMetadata={underwritingFormMetadata}
                formType="pre-underwriting"
                policyFundingSource={policyFundingSource}
                onStepSelect={setSelectedStepId}
                currentStep={latestStep as PolicyApplicationStep}
                stepStatus={stepToRender.status}
                onSkipForm={() => handleSkipUnderwritingForm('submit-pre-underwriting')}
                selectedSubmissionMethod={selectedSubmissionMethod}
                onSubmissionMethodChange={setSelectedSubmissionMethod}
              />
            ) : (
              <div className="text-red-600">
                Pre-underwriting form not available. Missing form configuration.
              </div>
            )}
          </StepContentWrapper>
        );
      }
      case 'application-form': {
        const actionVerb = getStepActionVerb(stepToRender.status);
        const isSkipping = policyApplicationStatus === 'savingStepStatus';
        const actions: StepAction[] = [
          {
            type: 'primary',
            label: `${actionVerb} Application`,
            onClick: () => handleStartForm(clientId!, applicationFormId!, 'application'),
            disabled: !policyApplication.stateOfResidenceAbbreviation || !applicationFormId,
          },
          {
            type: 'skip',
            label: 'Skip',
            onClick: () => handleSkipUnderwritingForm('application-form'),
            isLoading: isSkipping,
          },
        ];
        return (
          <StepContentWrapper
            title="Application Form"
            description={`Fill out the comprehensive application form to proceed with your ${fundingSourceDef.label} policy application.`}
            actions={actions}
            stepStatus={stepToRender.status}
          >
            {(!policyApplication.stateOfResidenceAbbreviation || !applicationFormId) && (
              <StepAlert
                title="Form Not Available"
                description={
                  !policyApplication.stateOfResidenceAbbreviation
                    ? 'State of residence is required to proceed with the application form.'
                    : 'Application form is not available for this state and policy type.'
                }
                variant="warning"
              />
            )}
          </StepContentWrapper>
        );
      }
      case 'sign-application-form': {
        const isSkipping = policyApplicationStatus === 'savingStepStatus';
        const actions: StepAction[] = [
          {
            type: 'skip',
            label: 'Skip',
            onClick: () => handleSkipUnderwritingForm('sign-application-form'),
            isLoading: isSkipping,
          },
          {
            type: 'test',
            label: 'Test',
            onClick: () => setIsTestDocusignDataModalOpen(!isTestDocusignDataModalOpen),
            isLoading: isSkipping,
          },
        ];
        return (
          <StepContentWrapper
            title="Sign Application Form"
            description="Track the signing progress of all required parties for your policy application."
            stepStatus={stepToRender.status}
            actions={actions}
          >
            <SignApplicationFormStep
              isTestDocusignDataModalOpen={isTestDocusignDataModalOpen}
              onOpenChange={(open: boolean) => setIsTestDocusignDataModalOpen(open)}
            />
          </StepContentWrapper>
        );
      }
      case 'preapplication-consent-form':
        return (
          <ConsentFormStep
            clientPolicyApplicationId={clientPolicyApplicationId}
            consentType="start"
            stepType="preapplication-consent-form"
            stepStatus={stepToRender.status}
          />
        );
      case 'consent-form':
        return (
          <ConsentFormStep
            clientPolicyApplicationId={clientPolicyApplicationId}
            consentType="submit"
            stepType="consent-form"
            stepStatus={stepToRender.status}
          />
        );

      case 'submit-application':
        return (
          <StepContentWrapper
            title=""
            description=""
            stepStatus={stepToRender.status}
          >
            {applicationFormId && applicationFormMetadata ? (
              <SubmitPolicyApplicationFormStep
                clientPolicyApplicationId={clientPolicyApplicationId}
                policyFormMetadata={applicationFormMetadata}
                formType="application"
                policyFundingSource={policyFundingSource}
                selectedSubmissionMethod={selectedSubmissionMethod}
                onSubmissionMethodChange={setSelectedSubmissionMethod}
              />
            ) : (
              <div className="text-red-600">
                Application form not available. Missing form configuration.
              </div>
            )}
          </StepContentWrapper>
        );

      default:
        return (
          <StepContentWrapper
            title={stepToRender?.title || 'Application Step'}
            description="This step is not yet configured. Please contact support for assistance."
            stepStatus={stepToRender.status}
          >
            <div className="flex items-center gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <AlertCircle className="h-12 w-12 flex-shrink-0 text-amber-500" />
              <div>
                <h4 className="font-semibold text-amber-800">Step Configuration</h4>
                <p className="text-amber-700">Step ID: {stepToRender?.stepType}</p>
              </div>
            </div>
          </StepContentWrapper>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b bg-white">
        <div className="container mx-auto px-4 py-6">
          <div className="mx-auto max-w-4xl">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {fundingSourceDef.carrier && (
                  <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                    <img
                      src={`/logos/${fundingSourceDef.carrier.toLowerCase()}.svg`}
                      alt={fundingSourceDef.carrier}
                      className="h-10 w-10"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <h1 className="text-2xl font-bold text-gray-900">{fundingSourceDef.label}</h1>
                  <p className="text-gray-600">Complete all steps to submit your application</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  {client.clientFirstName} {client.clientLastName}
                </span>
                {policyApplication.stateOfResidenceAbbreviation && (
                  <Badge variant="secondary">
                    {UnitedStatesState[
                      policyApplication.stateOfResidenceAbbreviation as keyof typeof UnitedStatesState
                    ]?.stateName || policyApplication.stateOfResidenceAbbreviation}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 flex items-center justify-between">
            <Button
              variant="ghost"
              className="rounded-full border bg-white p-2 text-gray-600 shadow-sm hover:bg-gray-100 hover:text-gray-900 sm:rounded-md sm:border-0 sm:bg-transparent sm:px-3 sm:py-2 sm:shadow-none"
              size="sm"
              onClick={handleGoHome}
            >
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Back to Application Review</span>
            </Button>

            {/* AI Underwriting Analysis Button - Show only for application-form step */}
            {clientId && applicationFormId && clientPolicyApplicationId && policyType && (
              <StepAiAnalysis
                clientId={clientId}
                formId={applicationFormId}
                clientPolicyApplicationId={clientPolicyApplicationId}
                policyType={policyType}
              />
            )}
          </div>

          <PolicyApplicationStepper
            steps={steps}
            currentStepId={
              allStepsCompleted ? 'application-complete' : (latestStep?.stepType ?? '')
            }
            selectedStepId={selectedStepId}
            onStepSelect={setSelectedStepId}
            renderStepContent={renderStepContent}
          />
        </div>
      </div>
      <div>
        <DialogModal
          open={showAIConsentDialog}
          onClose={() => setShowAIConsentDialog(!showAIConsentDialog)}
          title="AI-Powered Form Assistance"
          subTitle="We can use AI to help prefill your application based on
                    information you've already provided, saving you time and effort."
          primaryButton={
            <div className="flex w-full flex-wrap gap-4">
              <Button
                type="button"
                className="w-full py-2 text-sm font-semibold text-white sm:w-[calc(50%-0.5rem)]"
                onClick={handleAcceptAIPrefill}
                disabled={isPrefillLoading}
              >
                Yes, Prefill my application using AI
              </Button>
              <Button
                type="button"
                className="text-primary w-full py-2 text-sm font-semibold sm:w-[calc(50%-0.5rem)]"
                variant={'ghost'}
                onClick={handleDeclineAIPrefill}
                disabled={isPrefillLoading}
              >
                No, I&apos;ll fill it out manually
              </Button>
            </div>
          }
        />
      </div>
    </div>
  );
}
